import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Divider,
  Button,
  Badge,
  Flex,
  VStack,
  FormControl,
  FormLabel,
  Select,
  Image,
  Center,
} from '@chakra-ui/react';
import { formatDateAbbr } from '../../utils';
import DettaglioOrdine from './DettaglioOrdine';
import {
  filterRewardOrders,
  getItemFulfillmentDisplay,
  getRewardOrderTotalPoints,
  orderHasPendingPickup,
  resolveRewardLineImage,
} from '../../utils/rewardOrderUtils';
import { CIL_POINTS_LABEL, formatCilPoints } from '../../utils/cilPoints';
import InfoCILPoints from '../CILPoints/InfoCILPoints';
import {
  buildRewardImageByIdMap,
  fetchRewardSetsList,
} from '../../utils/rewardsApi';
import { getRewardImageUrl } from '../../utils/rewardMediaUrl';

const RewardOrderCard = ({
  reward,
  order,
  imageByRewardId,
  isSelected,
  onToggleDetails,
}) => {
  const media = resolveRewardLineImage(reward, imageByRewardId);
  const src = getRewardImageUrl(media);
  const itemStatus = getItemFulfillmentDisplay(
    reward.fulfillmentStatus || 'reserved'
  );
  const quantity = Number(reward.quantity) || 1;
  const linePoints =
    Number(reward.subtotalPoints) ||
    (Number(reward.pointsCost) || 0) * quantity;

  return (
    <Box
      bg="#36393f"
      borderRadius="xl"
      overflow="hidden"
      border="1px solid"
      borderColor={isSelected ? '#ffcc00' : 'gray.600'}
      display="flex"
      flexDirection="column"
      h="full"
      transition="all 0.25s ease"
      _hover={{
        borderColor: isSelected ? '#ffcc00' : 'gray.500',
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
      }}
    >
      <Box position="relative" w="full" pt="133.33%" bg="gray.700">
        {src ? (
          <Image
            src={src}
            alt={reward.name || 'Premio'}
            position="absolute"
            inset={0}
            w="full"
            h="full"
            objectFit="cover"
          />
        ) : (
          <Center position="absolute" inset={0} p={3}>
            <Text
              fontSize="sm"
              color="gray.400"
              textAlign="center"
              noOfLines={4}
              data-clarity-mask="True"
            >
              {reward.name}
            </Text>
          </Center>
        )}
        <Badge
          position="absolute"
          top={2}
          right={2}
          colorScheme={itemStatus.colorScheme}
          borderRadius="md"
          px={2}
          py={0.5}
          fontSize="xs"
          boxShadow="md"
        >
          {itemStatus.label}
        </Badge>
      </Box>

      <VStack align="stretch" spacing={2} p={4} flex={1}>
        <Heading
          as="h3"
          size="sm"
          color="white"
          noOfLines={2}
          lineHeight="short"
          data-clarity-mask="True"
        >
          {reward.name}
        </Heading>

        {quantity > 1 && (
          <Text fontSize="xs" color="gray.400">
            Quantità: {quantity}
          </Text>
        )}

        <Text fontSize="xs" color="gray.500">
          {formatDateAbbr(order.createdAt, { withTime: true })}
        </Text>

        <Text fontWeight="bold" color="purple.300" fontSize="md" mt="auto">
          {formatCilPoints(linePoints)}
        </Text>

        <Button
          size="sm"
          variant="outline"
          colorScheme={isSelected ? 'red' : 'blue'}
          w="full"
          onClick={onToggleDetails}
        >
          {isSelected ? 'Chiudi dettagli' : 'Dettagli ordine'}
        </Button>
      </VStack>
    </Box>
  );
};

const FULFILLMENT_FILTERS = [
  { value: 'all', label: 'Tutti' },
  { value: 'pending', label: 'Da ritirare' },
  { value: 'delivered', label: 'Ritirati' },
];

const matchesFulfillmentFilter = (order, filter) => {
  if (filter === 'all') return true;
  const pending = orderHasPendingPickup(order);
  if (filter === 'pending') return pending;
  if (filter === 'delivered') return !pending;
  return true;
};

const TuoiPremi = ({ userData }) => {
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [selectedYear, setSelectedYear] = useState('all');
  const [fulfillmentFilter, setFulfillmentFilter] = useState('all');
  const [rewardImageById, setRewardImageById] = useState({});

  useEffect(() => {
    let cancelled = false;
    fetchRewardSetsList()
      .then((sets) => {
        if (cancelled) return;
        setRewardImageById(buildRewardImageByIdMap(sets));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const rewardOrders = useMemo(
    () => filterRewardOrders(userData?.orders || []),
    [userData?.orders]
  );

  const availableYears = useMemo(() => {
    const years = new Set(
      rewardOrders
        .map((o) => o.createdAt && new Date(o.createdAt).getFullYear())
        .filter(Boolean)
    );
    return ['all', ...Array.from(years).sort((a, b) => Number(b) - Number(a))];
  }, [rewardOrders]);

  const displayedOrders = useMemo(() => {
    return rewardOrders
      .filter((order) => {
        const year = order.createdAt
          ? new Date(order.createdAt).getFullYear()
          : null;
        return (
          selectedYear === 'all' ||
          (year !== null && year === Number(selectedYear))
        );
      })
      .filter((order) => matchesFulfillmentFilter(order, fulfillmentFilter))
      .sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime()
      );
  }, [rewardOrders, selectedYear, fulfillmentFilter]);

  const rewardCards = useMemo(() => {
    const items = [];
    displayedOrders.forEach((order) => {
      (order.metadata?.rewards || []).forEach((reward, rewardIndex) => {
        items.push({
          key: `${order.documentId}-${reward.rewardId || rewardIndex}`,
          order,
          reward,
        });
      });
    });
    return items;
  }, [displayedOrders]);

  const expandedOrder = useMemo(
    () => displayedOrders.find((o) => o.documentId === expandedOrderId) || null,
    [displayedOrders, expandedOrderId]
  );

  const toggleOrderDetails = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const pendingCount = useMemo(
    () => rewardOrders.filter(orderHasPendingPickup).length,
    [rewardOrders]
  );

  return (
    <Box
      as="span"
      bg="#2f3136"
      p={{ base: 6, md: 8 }}
      display="block"
      borderRadius="20px"
      boxShadow="0 20px 40px rgba(0,0,0,0.3)"
      border="1px solid"
      borderColor="gray.600"
    >
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={2}
        flexWrap="wrap"
        gap={3}
      >
        <Box>
          <Flex align="center" gap={3} flexWrap="wrap">
            <Heading
              size="lg"
              color="white"
              id="tuoi-premi-title"
              fontWeight="800"
            >
              I tuoi premi
            </Heading>
            <InfoCILPoints variant="link" tone="account" size="xs" />
          </Flex>
          <Text color="gray.400" fontSize="sm" mt={1}>
            Premi riscattati con i {CIL_POINTS_LABEL} del tuo account. Il ritiro
            avviene in sede.
          </Text>
        </Box>
        {pendingCount > 0 && (
          <Badge
            colorScheme="orange"
            borderRadius="10px"
            px={3}
            py={1}
            fontSize="sm"
          >
            {pendingCount} da ritirare
          </Badge>
        )}
      </Flex>

      <Flex mt={4} mb={4} flexWrap="wrap" gap={4}>
        <FormControl width="auto" minW="120px">
          <FormLabel
            htmlFor="premi-filter-year"
            mb="0"
            fontSize="sm"
            color="gray.300"
          >
            Anno
          </FormLabel>
          <Select
            id="premi-filter-year"
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(e.target.value);
              setExpandedOrderId(null);
            }}
            size="md"
            bg="gray.700"
            color="white"
            borderColor="gray.600"
            _hover={{ borderColor: 'gray.500' }}
          >
            <option value="all">Tutti</option>
            {availableYears
              .filter((y) => y !== 'all')
              .map((y) => (
                <option
                  key={y}
                  value={y}
                  style={{ background: '#2f3136', color: 'white' }}
                >
                  {y}
                </option>
              ))}
          </Select>
        </FormControl>

        <FormControl width="auto" minW="160px">
          <FormLabel
            htmlFor="premi-filter-fulfillment"
            mb="0"
            fontSize="sm"
            color="gray.300"
          >
            Stato ritiro
          </FormLabel>
          <Select
            id="premi-filter-fulfillment"
            value={fulfillmentFilter}
            onChange={(e) => {
              setFulfillmentFilter(e.target.value);
              setExpandedOrderId(null);
            }}
            size="md"
            bg="gray.700"
            color="white"
            borderColor="gray.600"
            _hover={{ borderColor: 'gray.500' }}
          >
            {FULFILLMENT_FILTERS.map(({ value, label }) => (
              <option
                key={value}
                value={value}
                style={{ background: '#2f3136', color: 'white' }}
              >
                {label}
              </option>
            ))}
          </Select>
        </FormControl>
      </Flex>

      <Divider mb={6} borderColor="gray.600" />

      {rewardOrders.length === 0 ? (
        <Text color="gray.400" py={4}>
          Non hai ancora riscattato premi dal catalogo. Visita la sezione
          catalogo premi per usare i tuoi {CIL_POINTS_LABEL}.
        </Text>
      ) : displayedOrders.length === 0 ? (
        <Text color="gray.400" py={4}>
          Nessun ordine premio corrisponde ai filtri selezionati.
        </Text>
      ) : (
        <>
          <SimpleGrid
            columns={{ base: 1, sm: 2, md: 3, lg: 4 }}
            spacing={{ base: 4, md: 5 }}
          >
            {rewardCards.map(({ key, order, reward }) => (
              <RewardOrderCard
                key={key}
                reward={reward}
                order={order}
                imageByRewardId={rewardImageById}
                isSelected={expandedOrderId === order.documentId}
                onToggleDetails={() => toggleOrderDetails(order.documentId)}
              />
            ))}
          </SimpleGrid>

          {expandedOrder && (
            <Box mt={4}>
              <Text fontSize="sm" color="gray.400" mb={2}>
                Ordine del{' '}
                {formatDateAbbr(expandedOrder.createdAt, { withTime: true })}
                {' · '}
                {formatCilPoints(getRewardOrderTotalPoints(expandedOrder))}
              </Text>
              <DettaglioOrdine
                orders={displayedOrders}
                order={expandedOrder}
                onPaymentSuccess={() => setExpandedOrderId(null)}
                onHandleSaldo={() => {}}
                onHandleMainPagaOra={() => {}}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default TuoiPremi;
